/**
 * CCMSection - A horizontal scrolling section of CCM resources
 *
 * Each section has a distinct tile design based on its type:
 * - Capacity: Focus on role/expertise
 * - Care: Focus on wellness benefits
 * - Capital: Focus on funding details
 * - Community Model: Focus on network reach
 */
import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import GroupsIcon from '@mui/icons-material/Groups';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HubIcon from '@mui/icons-material/Hub';

import CapacityTile from './tiles/CapacityTile';
import CareTile from './tiles/CareTile';
import CapitalTile from './tiles/CapitalTile';
import CommunityTile from './tiles/CommunityTile';

// Icon mapping for section headers
const SECTION_ICONS = {
  GroupsIcon: GroupsIcon,
  FavoriteIcon: FavoriteIcon,
  AccountBalanceIcon: AccountBalanceIcon,
  HubIcon: HubIcon,
};

// Tile component mapping by section
const TILE_COMPONENTS = {
  capacity: CapacityTile,
  care: CareTile,
  capital: CapitalTile,
  communityModel: CommunityTile,
};

// eslint-disable-next-line max-lines-per-function
function CCMSection({
  sectionId,
  config,
  resources,
  onTileClick,
  adminMode,
  onRemoveTile,
  onMoveTile,
}) {
  const scrollContainerRef = React.useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const IconComponent = SECTION_ICONS[config.icon] || GroupsIcon;
  const TileComponent = TILE_COMPONENTS[sectionId] || CapacityTile;

  // Scroll handlers
  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  if (!resources || resources.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      {/* Section Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        mb: 2,
      }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: `${config.color}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconComponent sx={{ color: config.color, fontSize: 24 }} />
        </Box>
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: config.color,
              lineHeight: 1.2,
            }}
          >
            {config.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {config.subtitle}
          </Typography>
        </Box>

        {/* Scroll arrows */}
        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={handleScrollLeft}
            sx={{
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': { bgcolor: 'grey.100' },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleScrollRight}
            sx={{
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': { bgcolor: 'grey.100' },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Horizontal Scrolling Tiles */}
      <Box
        ref={scrollContainerRef}
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          pb: 2,
          px: isMobile ? 1 : 0.5,
          // Center tiles on mobile
          justifyContent: isMobile ? 'center' : 'flex-start',
          // Hide scrollbar but keep functionality
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          // Smooth scrolling
          scrollBehavior: 'smooth',
        }}
      >
        {resources.map((resource, index) => (
          <TileComponent
            key={resource.id}
            resource={resource}
            sectionColor={config.color}
            onClick={() => onTileClick(resource)}
            adminMode={adminMode}
            onRemove={() => onRemoveTile(sectionId, resource.id)}
            onMoveLeft={() => onMoveTile(sectionId, resource.id, 'left')}
            onMoveRight={() => onMoveTile(sectionId, resource.id, 'right')}
            isFirst={index === 0}
            isLast={index === resources.length - 1}
          />
        ))}
      </Box>
    </Box>
  );
}

export default CCMSection;
